// Chat file upload utilities — validation, upload, accepted types.
import { apiFetch } from '@/lib/api';
import { MAX_ATTACHMENT_BYTES, type StoredAttachment } from '@onestone/shared/chatAttachments';

const ACCEPTED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/avif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.ms-excel',
  'text/plain',
  'text/markdown',
  'text/html',
  'text/css',
  'text/csv',
  'text/xml',
  'text/x-python',
  'text/javascript',
  'text/typescript',
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-yaml',
  'application/x-sh',
  'application/sql',
  'application/toml',
  'application/octet-stream',
]);

const ACCEPTED_EXTENSIONS = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.svg',
  '.bmp',
  '.avif',
  '.ico',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.pptx',
  '.odt',
  '.ods',
  '.odp',
  '.rtf',
  '.txt',
  '.md',
  '.json',
  '.csv',
  '.xml',
  '.html',
  '.htm',
  '.css',
  '.js',
  '.jsx',
  '.ts',
  '.tsx',
  '.py',
  '.sh',
  '.bash',
  '.zsh',
  '.yaml',
  '.yml',
  '.toml',
  '.sql',
  '.log',
  '.ini',
  '.cfg',
  '.env',
  '.rs',
  '.go',
  '.java',
  '.c',
  '.cpp',
  '.h',
  '.hpp',
  '.rb',
  '.php',
  '.vue',
  '.svelte',
  '.graphql',
  '.proto',
  '.astro',
  '.lua',
  '.r',
  '.swift',
  '.kt',
  '.scala',
  '.clj',
  '.ex',
  '.exs',
  '.zig',
  '.wasm',
]);

/** `POST /api/chat/upload`: a record per staged file (its id names it in the message), errors per refused one. */
export interface UploadResult {
  files: StoredAttachment[];
  errors: string[];
}

export async function uploadFiles(fileList: File[]): Promise<UploadResult> {
  const form = new FormData();
  for (const f of fileList) form.append('files', f);
  const res = await apiFetch(`/api/chat/upload`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Upload failed (${res.status})`);
  }
  return (await res.json()) as UploadResult;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

export function validateFiles(files: File[]): { valid: File[]; errors: string[] } {
  const valid: File[] = [];
  const errors: string[] = [];
  for (const f of files) {
    const typeOk = f.type.length > 0 && ACCEPTED_TYPES.has(f.type);
    const extOk = ACCEPTED_EXTENSIONS.has(getExtension(f.name));
    if (!typeOk && !extOk) {
      const hint = f.type.length > 0 ? `type: ${f.type}` : 'unknown type';
      errors.push(`"${f.name}" — unsupported (${hint}). Allowed: images, PDFs, text, code, documents.`);
    } else if (f.size > MAX_ATTACHMENT_BYTES) {
      errors.push(
        `"${f.name}" — exceeds ${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB limit (${(f.size / 1024 / 1024).toFixed(1)} MB).`,
      );
    } else {
      valid.push(f);
    }
  }
  return { valid, errors };
}
